import dayjs from 'dayjs';
import 'dayjs/locale/pl';
import { useDispatch, useSelector } from 'react-redux';

import axios from 'axios';
import Benefit from 'layout/atoms/Benefit';
import Button from 'layout/atoms/Button';
import Container from 'layout/atoms/Container';
import ContentBox from 'layout/atoms/ContentBox';
import Grid from 'layout/atoms/Grid';
import ListHeader from 'layout/atoms/ListHeader/ListHeader';
import Switch from 'layout/atoms/Switch';
import SubscriptionPlanBox from 'layout/molecules/SubscriptionPlanBox';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import { SubscriptionPeriod } from 'containers/Subscription/types';
import Loader from 'layout/molecules/Loader';
import { Environment } from 'src/Environment';
import { AppDispatch } from 'store';
import { getUserSubscription, selectUserSubscription } from 'store/userSlice';

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const SubscriptionPage = () => {
    const dispatch = useDispatch<AppDispatch>();
    const userSubscription = useSelector(selectUserSubscription);
    const [period, setPeriod] = useState<SubscriptionPeriod>('quarter');
    const currentPlanIsFree = userSubscription.plan === 'FREE';
    const currentPlanIsTrial =
        userSubscription.plan === 'PREMIUM' && userSubscription.isTrial;
    const currentPlanIsPremium =
        userSubscription.plan === 'PREMIUM' && !userSubscription.isTrial;
    const currentPlanIsNotPremium =
        userSubscription.plan !== 'PREMIUM' || userSubscription.isTrial;

    const query = useQuery();
    const callbackReason = query.get('reason');

    useEffect(() => {
        userSubscription.status === 'IDLE' && dispatch(getUserSubscription());
    }, [dispatch, userSubscription.status]);

    const startPayment = (event: any) => {
        axios
            .post(
                `${Environment.backendUrl}/api/v1/user/plan/purchase`,
                {
                    period,
                },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            'hustleToken'
                        )}`,
                    },
                }
            )
            .then((response) => {
                if (response.data.redirectUrl) {
                    window.location.href = response.data.redirectUrl;
                }
            });
    };

    const freeBenefits = () => (
        <Grid columns="1" gap={12}>
            <Benefit active>5 projects</Benefit>
            <Benefit active>Unlimited tasks in project</Benefit>
            <Benefit active>2 automations</Benefit>
            <Benefit>Share and cooperate on projects</Benefit>
            <Benefit>24/7 Support</Benefit>
        </Grid>
    );

    const premiumBenefits = () => (
        <Grid columns="2" gap={12}>
            <Benefit active>Unlimited projects</Benefit>
            <Benefit active>Unlimited tasks in project</Benefit>
            <Benefit active>Unlimited automations</Benefit>
            <Benefit active>Share and cooperate on projects</Benefit>
            <Benefit active>24/7 Support</Benefit>
            <Benefit active={period === 'year'}>Saving 20%</Benefit>
        </Grid>
    );

    if (
        userSubscription.status === 'IDLE' ||
        userSubscription.status === 'LOADING'
    ) {
        return (
            <Container size="s">
                <ListHeader text="Subscription" />
                <Loader />
            </Container>
        );
    }

    return (
        <>
            <Container size="s">
                {callbackReason === 'cancel' && <>Payment canceled!</>}
                <Grid columns="1" gap={16}>
                    <ListHeader text="Subscription" />
                    <Grid columns="1" gap={16}>
                        <ContentBox>
                            <SubscriptionPlanBox
                                id="001"
                                monthPrice={userSubscription.plan || ''}
                                name={
                                    currentPlanIsTrial ? (
                                        <>
                                            <strong>Free Trial</strong>
                                        </>
                                    ) : null
                                }
                                pricePeriod={period}
                                current
                                benefits={
                                    !currentPlanIsFree && (
                                        <p>
                                            Valid unitl:{' '}
                                            <strong>
                                                {dayjs(
                                                    currentPlanIsTrial
                                                        ? userSubscription.trialEndDate
                                                        : userSubscription.expirationDate
                                                ).format('D MMMM YYYY')}
                                            </strong>
                                        </p>
                                    )
                                }
                                buttons={
                                    <>
                                        {currentPlanIsNotPremium && (
                                            <Button
                                                size="xxl"
                                                theme="primary"
                                                onClick={startPayment}
                                            >
                                                Upgrade to Premium
                                            </Button>
                                        )}
                                    </>
                                }
                            />
                        </ContentBox>
                    </Grid>
                </Grid>
            </Container>
            <Container size="l">
                <Grid columns="1" gap={16}>
                    <ListHeader text="Pricing" />
                    <Switch
                        name="period"
                        value={period === 'year'}
                        label="annualy"
                        leftLabel="Billed quarterly"
                        onChange={() =>
                            setPeriod(period === 'quarter' ? 'year' : 'quarter')
                        }
                    />
                    <Grid columns="5_7" gap={16}>
                        <ContentBox>
                            <SubscriptionPlanBox
                                id="001"
                                name={
                                    <>
                                        <strong>Free</strong>
                                    </>
                                }
                                monthPrice="0$"
                                pricePeriod={period}
                                benefits={freeBenefits()}
                            />
                        </ContentBox>
                        <ContentBox>
                            <SubscriptionPlanBox
                                id="002"
                                name={
                                    <>
                                        <strong>Premium</strong>
                                    </>
                                }
                                monthPrice="5$"
                                annualPrice="4$"
                                pricePeriod={period}
                                benefits={premiumBenefits()}
                                buttons={
                                    currentPlanIsNotPremium && (
                                        <Button
                                            size="xxl"
                                            theme="primary"
                                            onClick={startPayment}
                                        >
                                            Upgrade to Premium
                                        </Button>
                                    )
                                }
                            />
                        </ContentBox>
                    </Grid>
                </Grid>
            </Container>
        </>
    );
};

export default SubscriptionPage;
